import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigurationService } from '@ciam/library';
import { switchMap, timeout } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { ContactResponse } from 'src/app/shared/models/ContactResponse';
import { RegionValidationResponse } from 'src/app/shared/models/RegionValidationResponse';
import { Address, Contact, ContactRequest, Phone, PhoneAncillary } from 'src/app/shared/models/ContactRequest';
import { SessionStorageService } from 'src/app/shared/services/sessionStorage-service/session-storage.service';
import { LocaleService, OptimDecision, OptimService } from '@ciam/library';

export enum PARAMS {
  MOCK = 'mock',
  STATUS = 'contactStatus'
}

@Injectable({
  providedIn: 'root'
})
export class ContactService {
  constructor(
    private http: HttpClient,
    private config: ConfigurationService,
    private sessionStorageService: SessionStorageService,
    private optimService: OptimService,
    private localeService: LocaleService
  ) { }

  updateContactInformation(queryParams: any, contactParams: any, isAddSecondaryNumber?: boolean): Observable<ContactResponse> {
    const secondaryPhonePayloadV1 = {
      parsedContactAddress: {
        contact: {
          personName: {
            firstName: contactParams.firstName || '',
            lastName: contactParams.lastName || ''
          },
          emailAddress: contactParams.emailAddress || ''
        },
        contactAncillaryDetail: this.toAddSecondaryContactAncillaryDetails(contactParams)
      }
    };
    let url = this.config.select<string>('endpoints.contactInfoV1.url');
    let body = {};

    return this.optimService.decide$('wpro_2fa_on_edit', { country: `${this.localeService.solveLocale().country}` }).pipe(switchMap((result: OptimDecision) => {
      if (result.enabled) {
        body = secondaryPhonePayloadV1;
      } else {
        url = this.config.select<string>('endpoints.contactInfo.url');
        body = this.generateContactRequest(contactParams, isAddSecondaryNumber);
      }
      if (queryParams.params && queryParams.get(PARAMS.MOCK) === 'true') {
        if (queryParams.get(PARAMS.STATUS) === 'stateful') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        } else if (queryParams.get(PARAMS.STATUS) === '400') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        } else if (queryParams.get(PARAMS.STATUS) === '401') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        }
        else if (queryParams.get(PARAMS.STATUS) === '403') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        }
        else if (queryParams.get(PARAMS.STATUS) === '404') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        }
         else if (queryParams.get(PARAMS.STATUS) === '500') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        } else if (queryParams.get(PARAMS.STATUS) === '1050') {
          const status = queryParams.get(PARAMS.STATUS);
          url += `?contactStatus=${status}`;
        }
      }
      return this.http.put(url, body, { withCredentials: true });
    }));

  }

  generateContactRequest(contactParams: any, isAddSecondaryNumber: boolean): ContactRequest {
    const payload = {
      parsedContactAddress: {
        contact: this.toContact(contactParams),
        contactAncillaryDetail: this.toContactAncillaryDetail(contactParams),
        address: this.toAddress(contactParams)
      }
    };

    const secondaryPhonePayload = {
      parsedContactAddress: {
        contactAncillaryDetail: this.toAddSecondaryContactAncillaryDetails(contactParams)
      }
    };

    if (contactParams.currentPassword) {
      payload['password'] = contactParams.currentPassword;
    }

    return isAddSecondaryNumber ? secondaryPhonePayload : payload;
  }

  toContact(data: any): Contact {
    return {
      personName: {
        firstName: data.firstName || '',
        lastName: data.lastName || ''
      },
      companyName: this.sessionStorageService.getItem('companyName')?.toString(),
      phoneNumber: data.phoneNumber || '',
      emailAddress: data.email || ''
    };
  }

  toAddSecondaryContactAncillaryDetails(data: any): PhoneAncillary {
    const phones = [];

    phones.push({
      type: 'MOBILE',
      number: {
        countryCode: data.secondaryPhoneCountryCode || '',
        localNumber: data.secondaryPhoneNumber || ''
      }
    });

    return { phoneNumberDetails: phones };
  }

  toContactAncillaryDetail(data: any): PhoneAncillary {
    const phones = [];
    phones.push({
      type: 'HOME',
      number: {
        countryCode: data.phoneCountryCode || '',
        localNumber: data.phoneNumber || ''
      }
    } as Phone);

    phones.push({
      type: 'MOBILE',
      number: {
        countryCode: data.secondaryPhoneCountryCode || '',
        localNumber: data.secondaryPhoneNumber || ''
      }
    });

    phones.push({
      type: 'FAX',
      number: {
        countryCode: '',
        localNumber: ''
      }
    });

    return { phoneNumberDetails: phones };
  }

  toAddress(data: any): Address {
    return {
      streetLines: [data.addressLine1, data.addressLine2 ?? ''],
      city: data.city || '',
      stateOrProvinceCode: (data.stateOrProvince || '').toUpperCase(),
      postalCode: data.zipCode || '',
      countryCode: (data.countryCode || '').toUpperCase()
    };
  }

  validateRegion(postalCode: string, countryCode: string, stateCode: string): Observable<RegionValidationResponse> {
    const url = `${this.config.select<string>('endpoints.regionValidate.url')}?carriercode=FDXE&stateorprovincecode=${stateCode}&postalcode=${postalCode}`;
    const url1 = `&countrycode=${countryCode}&shipdate=${new Date().toISOString().slice(0, 10)}&routingcode=&checkformismatch=true`;
    return this.http.get(url + url1, { withCredentials: true }).pipe(timeout(Number(this.config.select<number>('endpoints.regionValidate.timeout'))));
  }
}
